import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  DataTable,
  EmptyState,
  Icon,
  Panel,
  Stat,
  useErrorDialog,
  type Column,
} from '@dayon/design-system';
import { formatCount, formatPesosCompact } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { AgentDetail } from './AgentDetail.tsx';
import { CommissionPlansDialog } from './CommissionPlansDialog.tsx';
import { NewAgentDialog } from './NewAgentDialog.tsx';
import { PortalAccessDialog } from './PortalAccessDialog.tsx';
import { SplitChangeQueue } from './SplitChangeQueue.tsx';
import { PayoutRunDialog } from '../payouts/PayoutRunDialog.tsx';
import { agentColumns, periodColumns } from './columns.tsx';
import type { AgentRow, PeriodRow, PlanRow } from './types.ts';
import {
  PENDING,
  captionLine,
  cadenceKey,
  inBranch,
  periodCadence,
  shortPeriod,
} from './vocabulary.tsx';

/**
 * Agent management — the partner network, seen from the platform side.
 *
 * Three levels and no deeper: Master, then Sub-agent, then Player. That is not
 * a screen rule this component enforces, it is a database CHECK
 * (agent_tier_parent_coherent: a master has no parent, a sub-agent has exactly
 * one; agent_not_own_parent blocks self-parenting), so the UI states it and
 * never re-litigates it.
 *
 * ── WHAT CHANGED, AND WHAT IS STILL TRUE ──────────────────────────────────
 * This module used to be read-only: three GET routes, "New agent" and "Approve
 * agent" rendered inert with their reason beside them, and a commission-plan
 * list that was a GROUP BY over the agents' own rates. All three now write —
 * `services/api/src/modules/agents/admin.ts` carries the routes.
 *
 * What is STILL true and still said on screen:
 *   · KYB is RECORDED, not run. No verification provider is integrated and none
 *     is stubbed, so approval is an operator writing down what they found.
 *   · There is no agent-side document table, so "2 documents outstanding" has
 *     no count to print.
 *   · The periods route is platform-wide and takes no brandId, so its totals do
 *     not move with the header's brand selector.
 *   · A plan's BANDS are now read by the settlement engine — `commission.close`
 *     resolves the band the period's commissionable revenue lands in and stores
 *     that rate on `agent_period_result.rate_bp`, whole-amount rather than
 *     marginal. This used to say the opposite, and the screen said so too; the
 *     claim moved when the engine did.
 *
 * ── THE CAPABILITY ────────────────────────────────────────────────────────
 * Reads are behind `partners.view`. Writes are behind `partners.manage`, a
 * capability added for exactly this: onboarding and governing a partner is not
 * the same authority as releasing a player's withdrawal, which is what the
 * `funds.approve` these writes used to borrow actually means.
 *
 * The settlement panel below is the exception and keeps `funds.approve`,
 * because `/api/admin/agents/periods` still guards on it — it reads when money
 * moves. Both keys are held by the same two roles today, so nothing changed for
 * anyone; the point is that the screen gate and the route gate name the SAME
 * capability, which is the only thing that stops them drifting apart.
 */
export function Agents() {
  const { brandId, brands, allowed } = useSession();
  const { showError } = useErrorDialog();
  const navigate = useNavigate();

  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [branch, setBranch] = useState('all');
  const [selected, setSelected] = useState<AgentRow | null>(null);
  /* The agent whose PORTAL LOGIN is being looked at. Its own state rather than
     a mode on `selected`: the dialog sits over the drawer, and closing one must
     not close the other. */
  const [portalFor, setPortalFor] = useState<AgentRow | null>(null);
  const [dialog, setDialog] = useState<'plans' | 'new' | null>(null);
  /** The period whose payout run is open, if any. */
  const [runPeriodId, setRunPeriodId] = useState<string | null>(null);
  /** Bumped by any write, to re-read what the write moved. */
  const [revision, setRevision] = useState(0);

  /*
   * Whether each read FAILED, as opposed to succeeding with nothing to return.
   *
   * The error dialog is dismissed in a second, and what was left behind it was
   * the copy written for an empty network — "This is a real zero — the network
   * is read live" — over a band of zeroes. That reads as "this brand has no
   * agents", which is a fact someone acts on; the truth was that nobody asked
   * the question successfully. Three flags rather than one because three
   * separate GETs back this screen and any one of them can fail alone: a dead
   * periods route must not put "could not be read" over a plans list that
   * arrived intact.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  const [plansFailed, setPlansFailed] = useState(false);
  const [periodsFailed, setPeriodsFailed] = useState(false);

  const canView = allowed('partners.view');
  /** The periods route guards on funds.approve, not partners.view — settlement
      is a money surface. Asking for it without the capability would 403 and pop
      an error dialog for a section the reader never asked to see. The writes in
      this module guard on the same key, for the same reason. */
  const canWrite = allowed('partners.manage');
  /*
   * Settlement is its own gate, and must stay its own gate.
   *
   * `/api/admin/agents/periods` guards on `funds.approve` — it reads when money
   * actually moves — while the partner writes moved to `partners.manage`. Both
   * are held by the same two roles today, so nothing changes for anyone now;
   * but reusing `canWrite` here would leave the panel visible to a future role
   * that holds partners.manage alone, and its fetch would 403. The screen gate
   * and the route gate have to name the same capability or they drift apart.
   */
  const canSeeSettlement = allowed('funds.approve');

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    const query = params.toString();

    api
      .get<{ items: AgentRow[] }>(`/api/admin/agents${query ? `?${query}` : ''}`)
      .then((d) => {
        if (cancelled) return;
        setAgents(d.items);
        setLoadFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Emptied deliberately: the previous brand's network sitting under a
        // failure notice would be read as this brand's answer.
        setAgents([]);
        setLoadFailed(true);
        showError({
          title: 'Could not load the agent network',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [brandId, canView, showError, revision]);

  /*
   * The plan book IS brand-scoped now, unlike the rate-grouping it replaced —
   * `agent.commission_plan` carries a brand, so a plan a scoped operator
   * cannot assign is a plan they are not shown. It is therefore refetched when
   * the header's brand scope changes, which the old route could not be.
   *
   * The periods route is NOT: it aggregates across the whole platform by
   * construction and takes no brandId. The panel says so rather than letting a
   * brand-scoped reader assume those figures moved with them.
   */
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    const query = params.toString();

    api
      .get<{ items: PlanRow[] }>(`/api/admin/agents/plans${query ? `?${query}` : ''}`)
      .then((d) => {
        if (cancelled) return;
        setPlans(d.items);
        setPlansFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setPlans([]);
        setPlansFailed(true);
        showError({
          title: 'Could not load commission plans',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [brandId, canView, showError, revision]);

  useEffect(() => {
    if (!canView) return;
    if (!canWrite) {
      // Never requested, so there is nothing to have failed: the panel explains
      // the missing capability instead, which is a different sentence again.
      setPeriods([]);
      setPeriodsFailed(false);
      return;
    }
    let cancelled = false;

    api
      .get<{ items: PeriodRow[] }>('/api/admin/agents/periods')
      .then((d) => {
        if (cancelled) return;
        setPeriods(d.items);
        setPeriodsFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setPeriods([]);
        setPeriodsFailed(true);
        showError({
          title: 'Could not load commission periods',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [canView, canWrite, showError, revision]);

  const masters = useMemo(() => agents.filter((a) => a.tier === 'MASTER'), [agents]);

  /**
   * The hierarchy rail, with DERIVED counts.
   *
   * The prototype hardcoded them, which is the one thing a hierarchy view must
   * not do: a rail that says "4" over a branch holding three agents is a
   * reconciliation break an operator will chase into the database.
   */
  const rail = useMemo(() => {
    const pending = agents.filter((a) => PENDING.has(a.status)).length;
    return [
      {
        key: 'all',
        icon: 'tree-structure',
        label: 'All agents',
        count: agents.length,
        meta: 'every agent in scope',
      },
      ...masters.map((m) => ({
        key: `master:${m.ref}`,
        icon: 'crown-simple',
        label: [m.ref, m.area].filter(Boolean).join(' · '),
        count: 1 + agents.filter((a) => a.parent_ref === m.ref).length,
        meta: 'master and downline',
      })),
      {
        key: 'sub',
        icon: 'share-network',
        label: 'Sub-agents',
        count: agents.filter((a) => a.tier === 'SUB_AGENT').length,
        meta: 'second level',
      },
      {
        key: 'pending',
        icon: 'hourglass',
        label: 'Pending approval',
        count: pending,
        meta: pending === 0 ? 'none waiting' : 'invited or in KYB',
      },
      {
        key: 'susp',
        icon: 'prohibit',
        label: 'Suspended',
        count: agents.filter((a) => a.status === 'SUSPENDED').length,
        meta: 'players keep playing',
      },
    ];
  }, [agents, masters]);

  /** A master branch selected under one brand does not exist under another.
      Falling back beats leaving the reader staring at an empty table wondering
      whether the branch is empty or gone. */
  const activeBranch = rail.some((r) => r.key === branch) ? branch : 'all';
  const visible = useMemo(
    () => agents.filter((a) => inBranch(activeBranch, a)),
    [agents, activeBranch],
  );

  /**
   * The KPI band.
   *
   * Summed from the rows the table is showing — the same brand scope and the
   * same hierarchy branch — so an operator can add the column up and land on
   * the band. The prototype achieved that by summing the ROUNDED per-row ₱M
   * figures; that trick only works while every row is millions-scale, and real
   * rows here span ₱0 to a few hundred thousand, where rounding each row to
   * 0.1M would zero the entire band. So the exact centavos are summed and the
   * caption says which rows they came from.
   */
  const totals = useMemo(() => {
    let ggr = 0n;
    let commission = 0n;
    let players = 0;
    for (const a of visible) {
      ggr += BigInt(a.ggr_30d);
      commission += BigInt(a.commission);
      players += Number(a.players);
    }
    return { ggr, commission, players };
  }, [visible]);

  const pendingCount = visible.filter((a) => PENDING.has(a.status)).length;
  const pendingKyb = visible.filter((a) => a.status === 'PENDING_KYB').length;
  const allPending = agents.filter((a) => PENDING.has(a.status)).length;
  const brandName = brands.find((b) => b.id === brandId)?.name ?? null;

  const cadence = periodCadence(periods);

  /*
   * The periods table, plus the column that opens the run.
   *
   * Composed here rather than in columns.tsx because the action needs this
   * screen's state, and a column file that reaches for a setter is a column
   * file that can only ever be used by one screen.
   *
   * An OPEN period gets no control at all: its figures can still move, so
   * there is nothing to pay against it and offering the button would be
   * offering an action the API refuses.
   */
  const periodColumnsWithRun: Column<PeriodRow>[] = [
    ...periodColumns,
    {
      key: 'run',
      header: 'Payout run',
      render: (p) =>
        p.status === 'OPEN' ? (
          <span className="text-subtle">still accruing</span>
        ) : (
          <Button variant="secondary" onClick={() => setRunPeriodId(p.id)}>
            {p.status === 'PAID' ? 'View payouts' : 'Payout run'}
          </Button>
        ),
    },
  ];
  const openPeriod = periods.find((p) => p.status === 'OPEN') ?? null;
  const unpaidPeriods = periods.filter((p) => p.status !== 'PAID').length;
  /** Periods whose window is shorter than the cadence they carry. */
  const shortPeriods = periods.filter((p) => shortPeriod(p) !== null);

  /** One phrase, used by the drawer and the plans list, so the two cannot
      disagree about when an agent is actually paid. */
  const settlement: ReactNode = !canSeeSettlement ? (
    <span className="text-subtle">needs funds.approve</span>
  ) : periodsFailed ? (
    <span className="text-subtle">periods not read</span>
  ) : cadence ? (
    `${cadence}${openPeriod ? ` · open period ${openPeriod.ref}` : ''}`
  ) : (
    <span className="text-subtle">no periods issued yet</span>
  );

  /** Plans whose own settlement rule disagrees with the periods actually issued. */
  const measured = cadenceKey(cadence);
  const settlementConflicts = measured
    ? plans.filter((p) => p.status === 'ACTIVE' && p.settlement !== measured)
    : [];

  const columns = useMemo(() => agentColumns((a) => setSelected(a)), []);
  const refresh = useCallback(() => setRevision((n) => n + 1), []);

  if (!canView) {
    return (
      <EmptyState icon="lock-key" title="Agent management">
        This module needs the partners.view capability. Your role does not hold it.
      </EmptyState>
    );
  }

  return (
    <>
      {/*
        THE PURPOSE BELONGS HERE, AND NOT IN EITHER NOTICE BELOW IT.

        This screen already opens with two quiet notices, and both of them earn
        their place: the first carries the rules an operator has to hold in mind
        while reading the table — three levels and no deeper, what suspending a
        master does and does not touch, and that the commission shown is accrued
        and unpaid — and the second says that nothing verifies an agent
        automatically, so approval is a person writing down what they found.

        Adding "what this screen is for" as a third stacked paragraph would have
        buried all three. Prose blocks in a column are read as one wall and
        skipped as one wall, and the sentence most needed by somebody who has
        never been here before is the one that would have been skipped with it.
        The header is the one place a sentence about the SCREEN can sit without
        competing with a rule about the DATA.
      */}
      <ScreenHeader
        title="Agent management"
        purpose="For the team that runs the partner network: take on new agents, decide who stays active, and see what each has earned but not yet been paid."
        /*
          The caption kept exactly as it was, all three parts of it.

          The middle part is tri-state and the three states are not
          interchangeable. "Loading…" means the answer has not arrived yet; "the
          network could not be read" means the request failed, so the counts
          that would have stood here were never made; and a count means the
          network answered. Collapsing that middle state into a zero is the
          precise mistake the long note beside `loadFailed` above exists to
          prevent — "this brand has no agents" is a fact somebody acts on, and
          "nobody asked the question successfully" is not — and the rail, the KPI
          band and the table's empty state all lean on the same flag for the same
          reason.

          "3-level hierarchy" leads and the brand scope trails because both are
          true of all three states: a failed read still failed inside a brand
          scope, and a reader who does not know whether they are looking at one
          brand or at all of them cannot judge the number either way.
        */
        meta={
          <>
            3-level hierarchy ·{' '}
            {loading
              ? 'Loading…'
              : loadFailed
                ? 'the network could not be read'
                : `${formatCount(agents.length)} agents · ${
                    allPending > 0
                      ? `${formatCount(allPending)} pending approval`
                      : 'none pending approval'
                  }`}{' '}
            · {brandName ?? 'all brands'}
          </>
        }
        actions={
          <>
            <Button onClick={() => setDialog('plans')}>
              <Icon name="percent" />
              Commission plans
            </Button>
            {/* Hidden rather than greyed for a reader who cannot write, which is
                the rule the second notice below states out loud. A disabled
                button with its reason in a tooltip is a reason most browsers
                never show and no touch device shows at all. */}
            {canWrite && (
              <Button variant="primary" onClick={() => setDialog('new')}>
                <Icon name="plus" />
                New agent
              </Button>
            )}
          </>
        }
      />

      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          Master → Sub-agent → Player, three levels and no deeper. A sub-agent cannot be re-parented
          once it has players, and suspending a master cascades to its sub-agents but never blocks
          player logins — both are enforced by the platform itself, not just by this screen.
          Commission on this screen is{' '}
          <strong>accrued for the period and unpaid</strong>; payouts are settled in the
          Transactions module.
        </div>
      </div>

      {/* ── Hierarchy ─────────────────────────────────────────────────────
          The design draws this as a left rail. It is rendered as the design
          system's queue grid instead: same single-select semantics, same icon
          and count per branch, but it reflows to one column on a phone where a
          fixed rail would have squeezed an eight-column table into a gutter. */}
      <Panel title="Hierarchy">
        <div className="queue-grid" role="radiogroup" aria-label="Filter the table by branch">
          {rail.map((item) => (
            <button
              key={item.key}
              type="button"
              role="radio"
              aria-checked={item.key === activeBranch}
              className={item.key === activeBranch ? 'queue-pill attention' : 'queue-pill'}
              onClick={() => setBranch(item.key)}
            >
              <span className="queue-pill-label">
                <Icon name={item.icon} />
                {item.label}
              </span>
              {/* A branch count is a count OF rows. With no rows read there is
                  nothing to count, and a rail of zeroes would say the branches
                  are empty when they are unknown. */}
              <span className="queue-pill-value">{loadFailed ? '—' : formatCount(item.count)}</span>
              <span className="queue-pill-meta">{loadFailed ? 'not read' : item.meta}</span>
            </button>
          ))}
        </div>
      </Panel>

      <div className="stat-grid">
        {/* Every tile here is summed from rows. A failed read has no rows, so
            they say nothing rather than saying zero. */}
        <Stat
          label="Agent GGR 30d"
          value={loadFailed ? '—' : formatPesosCompact(totals.ggr)}
          delta={loadFailed ? 'not summed — the read failed' : 'settled rounds, last 30 days'}
        />
        <Stat
          label="Commission accrued"
          value={loadFailed ? '—' : formatPesosCompact(totals.commission)}
          delta={loadFailed ? 'not summed — the read failed' : 'unpaid'}
        />
        <Stat
          label="Players via agents"
          value={loadFailed ? '—' : formatCount(totals.players)}
          delta={
            loadFailed ? 'not summed — the read failed' : `${formatCount(visible.length)} agents`
          }
        />
        <Stat
          label="Pending approval"
          value={loadFailed ? '—' : formatCount(pendingCount)}
          delta={
            loadFailed
              ? 'not counted — the read failed'
              : pendingCount === 0
                ? 'none waiting'
                : [
                    pendingKyb > 0 ? `${formatCount(pendingKyb)} in KYB` : null,
                    pendingCount - pendingKyb > 0
                      ? `${formatCount(pendingCount - pendingKyb)} invited`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
          }
        />
      </div>
      <div className="text-subtle" style={captionLine}>
        {loadFailed ? (
          <>
            Nothing was summed — the agent read failed, so the band above stands over no rows at all
            rather than over an empty network.{' '}
          </>
        ) : (
          <>
            Summed from the {formatCount(visible.length)} agent row
            {visible.length === 1 ? '' : 's'} in view, so the band reconciles with the table under
            you.{' '}
          </>
        )}
        No &ldquo;% of platform&rdquo; figure is shown: this screen is never given the whole
        platform&rsquo;s GGR over the same window and brand scope to divide by, and a share
        computed against a different window is a number an operator would quote in a board pack.
      </div>

      <div className="notice notice-quiet">
        <Icon name="warning-circle" />
        <div>
          <strong>KYB is recorded by hand.</strong> Creating an agent, approving one, suspending one
          and putting one on a commission plan are all real writes now, each with its reason in the
          audit trail. What is <em>not</em> real is automated verification: no identity-verification
          provider is integrated with this platform and none is stubbed, so a new agent waits in{' '}
          <strong>Pending</strong> until an operator reads the evidence and records the outcome.
          {!canWrite && (
            <>
              {' '}
              Your role holds <strong>partners.view</strong> and not{' '}
              <strong>partners.manage</strong>, so this screen is read-only for you and the write
              controls are hidden rather than greyed.
            </>
          )}
        </div>
      </div>

      <Panel>
        {loading && agents.length === 0 ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Agents in the selected branch, with parent, tier, status, live players, 30-day GGR and accrued commission"
            columns={columns}
            rows={visible}
            rowKey={(a) => a.id}
            empty={
              <EmptyState
                icon={loadFailed ? 'warning-circle' : 'share-network'}
                title={
                  loadFailed ? 'The agent network could not be read' : 'No agents in this branch'
                }
                action={
                  loadFailed || activeBranch === 'all' ? undefined : (
                    <Button onClick={() => setBranch('all')}>Show all agents</Button>
                  )
                }
              >
                {/* The failed read answers first, and it answers with the
                    failure — never with a zero. "There are no agents" and "we
                    could not ask" are different facts about the network, and
                    only one of them is safe to act on.

                    The read is `GET /api/admin/agents`. It used to be named in
                    the sentence below and is in this comment instead: an
                    operator cannot open a path, cannot quote it to anybody who
                    will act on it, and learns nothing from it that "the list
                    could not be read" does not say better. */}
                {loadFailed
                  ? `The agent list could not be read, so this table is empty because nothing came back — not because ${brandName ?? 'the platform'} has no agents. The message that appeared carries what the platform said; try again shortly.`
                  : activeBranch === 'all'
                    ? `There are no agents in ${brandName ?? 'any brand'} yet. This is a real zero — the network is read live.`
                    : 'This branch of the hierarchy is empty under the current brand scope.'}
              </EmptyState>
            }
          />
        )}
      </Panel>

      {/* ── Split change requests ─────────────────────────────────────────
          The operator's half of `POST /api/agent/downline/split`. The portal
          writes a request and cannot move a rate — `dayon_partner` holds no
          UPDATE on `agent.agent` at all — so until this panel existed the row
          was recorded and never acted on, and the portal said so out loud.

          Behind `partners.view` like the rest of the reads on this screen; the
          two verdicts need `partners.manage`, and the panel says so in text
          rather than greying a button whose tooltip a browser would suppress. */}
      <SplitChangeQueue
        brandId={brandId}
        canWrite={canWrite}
        revision={revision}
        onChanged={refresh}
      />

      {/* ── Settlement periods ────────────────────────────────────────────
          Not in the design, but it is what makes "accrued, unpaid" legible: it
          names which periods the accrual spans and when they close. */}
      <Panel title="Commission periods">
        {!canWrite ? (
          <EmptyState icon="lock-key" title="Settlement is a money surface">
            Commission periods need funds.approve, which your role does not hold. The accrued
            figures above are still yours to read — what is hidden is when they settle.
          </EmptyState>
        ) : periodsFailed ? (
          <EmptyState icon="warning-circle" title="The commission periods could not be read">
            The commission periods could not be fetched, so this panel is empty because nothing was
            read. Whether a period is open, and when the accrual above settles, is unknown from here
            — not answered none. It is its own request, so the rest of this screen is read
            separately, so this failure says nothing about it either way.
          </EmptyState>
        ) : periods.length === 0 ? (
          /* THIS BRANCH USED TO NAME THE TABLE IT HAD READ.

             It said "Nothing has been opened in agent.commission_period, so no
             accrual has a window and no payout has a due date". The periods list
             is read from `agent.commission_period`, which is genuinely where the
             screen learned this, and that is worth recording — here, for whoever
             next reads the source. It is worth nothing to the operator in front
             of the panel: a table name is not something they can look up, ask
             anybody for, or repeat to the partner chasing a statement.

             THE CLAIM IS THE VALUABLE HALF AND IS KEPT WHOLE. It was saying two
             things at once and both still have to be said. First, that the read
             SUCCEEDED and came back with nothing — which is the one thing that
             separates this branch from the one directly above it, where the read
             failed and also came back with nothing, and the two are not the same
             fact about the partner network. Second, the consequence: the accrued
             commission in the band further up this screen is real money with no
             window around it and no date attached to it, which is what an
             operator actually came to this panel to find out. */
          <EmptyState icon="calendar-blank" title="No commission periods yet">
            No settlement period has been opened yet. This panel was read successfully and nothing
            was filtered out — there is genuinely none recorded, so the commission accrued above has
            no window around it and no payout date attached to it yet.
          </EmptyState>
        ) : (
          <>
            <DataTable
              caption="Commission periods with status, agent count, net revenue, accrued commission, withheld commission and the payout run"
              columns={periodColumnsWithRun}
              rows={periods}
              rowKey={(p) => p.id}
            />
            <div className="text-subtle" style={captionLine}>
              Platform-wide and <strong>not</strong> brand-scoped — the periods are read for every
              brand at once, so these totals do not move with the header&rsquo;s brand selector and will not tie to
              the band above. The <strong>Commission accrued</strong> figure spans every period not
              yet marked Paid, currently {formatCount(unpaidPeriods)}, not just the open one.
              {cadence ? ` Periods run ${cadence.toLowerCase()}.` : ''}
            </div>
            {/* A period shorter than its own cadence is a BOUNDARY CORRECTION,
                not an error: a chain cut on UTC midnight is snapped back onto
                the Manila gaming day by one short period, and every period
                after it is whole. An agent whose statement covers six days
                rather than seven is entitled to see that said somewhere. */}
            {shortPeriods.length > 0 && (
              <div className="text-subtle" style={captionLine}>
                {shortPeriods.map((p) => p.ref).join(', ')}{' '}
                {shortPeriods.length === 1 ? 'is' : 'are'} shorter than a full{' '}
                {(cadence ?? 'period').toLowerCase()} window — a one-off correction where the chain
                was re-cut onto the Asia/Manila gaming-day boundary. No revenue falls between two
                periods: each one begins exactly where the last ended.
              </div>
            )}
            {cadence && cadence !== 'Monthly' && (
              <div className="notice notice-quiet">
                <Icon name="warning-circle" />
                <div>
                  <strong>Cadence conflict, unresolved.</strong> The plan says &ldquo;Monthly · 5th
                  working day&rdquo; wherever settlement appears, but the periods the platform
                  has actually issued are {cadence.toLowerCase()}. This screen prints what the data
                  says. One of the two is stale and it has to be settled before an agent is told a
                  payment date.
                  {settlementConflicts.length > 0 && (
                    <>
                      {' '}
                      {formatCount(settlementConflicts.length)} commission plan
                      {settlementConflicts.length === 1 ? '' : 's'} now promise
                      {settlementConflicts.length === 1 ? 's' : ''} a different cadence again —{' '}
                      {settlementConflicts.map((p) => p.ref).join(', ')} — which is a promise nobody
                      is keeping.
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </Panel>

      {selected && (
        <AgentDetail
          agent={selected}
          settlement={settlement}
          canWrite={canWrite}
          plans={plans}
          onClose={() => setSelected(null)}
          onChanged={refresh}
          onViewPlayers={() => {
            setSelected(null);
            navigate('/players');
          }}
          onPortalAccess={() => setPortalFor(selected)}
        />
      )}

      {/* Keyed so re-opening it for a different agent starts from that agent's
          state rather than the previous one's, and so a link shown once is
          never shown again beside the wrong partner. */}
      <PortalAccessDialog
        key={portalFor?.id ?? 'none'}
        agent={portalFor}
        canWrite={canWrite}
        onClose={() => setPortalFor(null)}
        onDone={refresh}
      />

      <CommissionPlansDialog
        open={dialog === 'plans'}
        onClose={() => setDialog(null)}
        plans={plans}
        plansFailed={plansFailed}
        brands={brands}
        brandId={brandId}
        canWrite={canWrite}
        measuredCadence={cadence}
        onChanged={refresh}
      />

      {/* The step the worker deliberately stops before: `commission.close`
          never marks a period PAID, because a payment needs an approver and a
          job is nobody. This is where a person picks it up. */}
      <PayoutRunDialog
        domain="agents"
        periodId={runPeriodId}
        onClose={() => setRunPeriodId(null)}
        onDone={refresh}
      />

      <NewAgentDialog
        open={dialog === 'new'}
        onClose={() => setDialog(null)}
        masters={masters}
        brands={brands}
        brandId={brandId}
        plans={plans}
        loadFailed={loadFailed}
        onCreated={() => {
          setDialog(null);
          refresh();
        }}
      />
    </>
  );
}
